import React, { useState, useEffect } from 'react';
import { Loader, ArrowRight, RefreshCw } from 'lucide-react';
import { useIdeaStore } from '../../../stores/ideaStore';
import { useIdeaGenerationStore } from '../../../stores/ideaGenerationStore';
import { generateAIIdeas } from '../../../utils/ai';
import { TRANSITIONS } from '../../../utils/animations';
import IdeaCard from './ideas/IdeaCard';
import type { Platform } from '../../../stores/socialMediaStore';
import toast from 'react-hot-toast';

interface QuickIdeasGeneratorProps {
  connectedAccounts: Array<{
    platform: Platform;
    connected: boolean;
  }>;
  showFullPage?: () => void;
}

export default function QuickIdeasGenerator({ connectedAccounts, showFullPage }: QuickIdeasGeneratorProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { ideas, addIdeas, clearIdeas } = useIdeaStore();
  const { canGenerateIdeas, incrementUsage, currentMonthUsage, getMonthlyLimit } = useIdeaGenerationStore();
  const monthlyLimit = getMonthlyLimit();

  const handleGenerateIdeas = async () => {
    if (!canGenerateIdeas()) {
      toast.error(`Você atingiu o limite de ${monthlyLimit} gerações de ideias para este mês`);
      return;
    }

    setIsLoading(true);
    clearIdeas();
    
    try {
      const connectedPlatforms = connectedAccounts
        .filter(account => account.connected)
        .map(account => account.platform);

      const newIdeas = await generateAIIdeas(connectedPlatforms);
      
      if (incrementUsage()) {
        addIdeas(newIdeas);
      }
    } catch (error) {
      console.error('Error generating ideas:', error);
      toast.error('Erro ao gerar ideias. Por favor, tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Generate initial ideas on mount if allowed
  useEffect(() => {
    if (canGenerateIdeas() && ideas.length === 0) {
      handleGenerateIdeas();
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border-2 border-dashed border-indigo-200 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-indigo-100 rounded-full opacity-30"></div>
        <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-purple-100 rounded-full opacity-30"></div>
        
        <div className="relative">
          <div className="flex items-center justify-between mb-4">
            <p className="text-gray-700 font-medium">
              {connectedAccounts.length > 0 
                ? `Ideias personalizadas para ${connectedAccounts
                    .filter(a => a.connected)
                    .map(a => a.platform)
                    .join(', ')}`
                : "Conecte suas redes sociais para ideias mais relevantes"}
            </p>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              <p className="text-sm text-gray-500">
                {currentMonthUsage} de {monthlyLimit} gerações este mês
              </p>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={handleGenerateIdeas}
              disabled={isLoading || !canGenerateIdeas()}
              className={`flex-1 py-3 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 ${TRANSITIONS.BUTTON} flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105`}
            >
              {isLoading ? (
                <>
                  <Loader className="animate-spin h-5 w-5 mr-2" />
                  Gerando novas ideias...
                </>
              ) : (
                <>
                  Gerar Novas Ideias
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
            
            {ideas.length > 0 && (
              <button
                onClick={handleGenerateIdeas}
                disabled={isLoading || !canGenerateIdeas()}
                className="p-3 bg-white text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Regenerar ideias"
              >
                <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4 transition-all duration-300">
        {ideas.map((idea, index) => (
          <div
            key={`${idea.title}-${index}`}
            className={`${TRANSITIONS.SLIDE_UP}`}
            style={{
              animationDelay: `${index * 100}ms`,
            }}
          >
            <IdeaCard idea={idea} />
          </div>
        ))}
      </div>

      {connectedAccounts.length === 0 && ideas.length === 0 && (
        <div className="text-center p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <Instagram className="h-8 w-8 text-gray-400" />
          </div>
          <p className="text-gray-700 font-medium mb-4">
            Conecte suas redes sociais para receber ideias personalizadas
          </p>
          {showFullPage && (
            <button
              onClick={showFullPage}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors inline-flex items-center"
            >
              Ir para Configurações
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}